import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../Icon";
import PillSelect from "./PillSelect";
import {
    MOCK_TAGS,
    MOCK_TYPES,
    TICKET_PRIORITY_OPTIONS,
    TICKET_STATUS_OPTIONS,
} from "./mockData";

const STATUS_OPTIONS = TICKET_STATUS_OPTIONS.filter((o) => o.value);
const TYPE_OPTIONS = MOCK_TYPES.filter((o) => o.value);
const TAG_OPTIONS = [{ value: "", label: "Select...", color: "#9ca3af" }, ...MOCK_TAGS.filter((o) => o.value)];

const RequiredMark = () => (
    <span className="clk-mk-required" aria-hidden="true">*</span>
);

const buildInitialForm = (departments) => ({
    title: "",
    description: "",
    status: "open",
    department: departments[0]?.name || "",
    assignee: "",
    typeKey: "",
    priorityKey: "medium",
    tagKey: "",
});

const MockNewTicketModal = ({
    departments = [],
    assigneeOptions = [],
    onClose,
    onSave,
}) => {
    const titleRef = useRef(null);
    const [form, setForm] = useState(() => buildInitialForm(departments));
    const [error, setError] = useState("");

    const departmentOptions = useMemo(
        () => departments.map((d) => ({ value: d.name, label: d.name, color: d.color })),
        [departments]
    );

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            setError("Title is required.");
            titleRef.current?.focus();
            return;
        }
        if (!form.description.trim()) {
            setError("Description is required.");
            return;
        }
        if (!form.status) {
            setError("Status is required.");
            return;
        }
        if (!form.department) {
            setError("Department is required.");
            return;
        }

        onSave({
            title: form.title.trim(),
            description: form.description.trim(),
            status: form.status,
            department: form.department,
            assignee: form.assignee || null,
            typeKey: form.typeKey,
            priorityKey: form.priorityKey,
            tagKey: form.tagKey,
        });
    };

    const updateField = (field) => (value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setError("");
    };

    return (
        <div className="clk-mk-modal-backdrop" onClick={onClose} role="presentation">
            <div
                className="clk-mk-modal clk-mk-new-ticket-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="clk-mk-new-ticket-title"
            >
                <form onSubmit={handleSubmit}>
                    <div className="clk-mk-new-ticket-head">
                        <h2 id="clk-mk-new-ticket-title" className="clk-mk-new-ticket-title">New Ticket</h2>
                        <button type="button" className="clk-mk-new-ticket-close" onClick={onClose} aria-label="Close" />
                    </div>

                    <div className="clk-mk-new-ticket-body">
                        <div className="clk-mk-new-ticket-field">
                            <label className="clk-mk-new-ticket-label" htmlFor="new-ticket-title">
                                <b>Title</b>
                                <RequiredMark />
                            </label>
                            <input
                                ref={titleRef}
                                id="new-ticket-title"
                                type="text"
                                className="clk-mk-new-ticket-input"
                                placeholder="Enter title"
                                value={form.title}
                                onChange={(e) => updateField("title")(e.target.value)}
                                required
                            />
                        </div>

                        <div className="clk-mk-new-ticket-field">
                            <label className="clk-mk-new-ticket-label" htmlFor="new-ticket-description">
                                <b>Description</b>
                                <RequiredMark />
                            </label>
                            <textarea
                                id="new-ticket-description"
                                className="clk-mk-new-ticket-textarea"
                                placeholder="Please enter description"
                                rows={5}
                                value={form.description}
                                onChange={(e) => updateField("description")(e.target.value)}
                                required
                            />
                        </div>

                        <div className="clk-mk-new-ticket-row">
                            <div className="clk-mk-new-ticket-col">
                                <span className="clk-mk-new-ticket-label">
                                    <b>Status</b>
                                    <RequiredMark />
                                </span>
                                <PillSelect
                                    value={form.status}
                                    options={STATUS_OPTIONS}
                                    onChange={updateField("status")}
                                    ariaLabel="Ticket status"
                                />
                            </div>
                            <div className="clk-mk-new-ticket-col">
                                <span className="clk-mk-new-ticket-label">
                                    <b>Department</b>
                                    <RequiredMark />
                                </span>
                                <PillSelect
                                    value={form.department}
                                    options={departmentOptions}
                                    onChange={updateField("department")}
                                    ariaLabel="Ticket department"
                                />
                            </div>
                        </div>

                        <div className="clk-mk-new-ticket-row">
                            <div className="clk-mk-new-ticket-col">
                                <span className="clk-mk-new-ticket-label">
                                    <b>Assignee</b>
                                </span>
                                <PillSelect
                                    value={form.assignee}
                                    options={assigneeOptions}
                                    onChange={updateField("assignee")}
                                    ariaLabel="Ticket assignee"
                                    showAvatar
                                />
                            </div>
                            <div className="clk-mk-new-ticket-col">
                                <span className="clk-mk-new-ticket-label">
                                    <b>Type</b>
                                </span>
                                <PillSelect
                                    value={form.typeKey}
                                    options={[{ value: "", label: "Select...", color: "#9ca3af" }, ...TYPE_OPTIONS]}
                                    onChange={updateField("typeKey")}
                                    ariaLabel="Ticket type"
                                    showDot={Boolean(form.typeKey)}
                                />
                            </div>
                        </div>

                        <div className="clk-mk-new-ticket-row">
                            <div className="clk-mk-new-ticket-col">
                                <span className="clk-mk-new-ticket-label">
                                    <b>Priority</b>
                                </span>
                                <PillSelect
                                    value={form.priorityKey}
                                    options={TICKET_PRIORITY_OPTIONS}
                                    onChange={updateField("priorityKey")}
                                    ariaLabel="Ticket priority"
                                />
                            </div>
                            <div className="clk-mk-new-ticket-col">
                                <span className="clk-mk-new-ticket-label">
                                    <b>Tags</b>
                                </span>
                                <PillSelect
                                    value={form.tagKey}
                                    options={TAG_OPTIONS}
                                    onChange={updateField("tagKey")}
                                    ariaLabel="Ticket tags"
                                    showDot={Boolean(form.tagKey)}
                                />
                            </div>
                        </div>

                        <div className="clk-mk-new-ticket-row">
                            <div className="clk-mk-new-ticket-col">
                                <span className="clk-mk-new-ticket-label">
                                    <b>Attachments</b>
                                </span>
                                <button
                                    type="button"
                                    className="clk-mk-new-ticket-attach-btn"
                                    disabled
                                    title="Not available in demo"
                                >
                                    <Icon name="paperclip" size={16} />
                                </button>
                            </div>
                            <div className="clk-mk-new-ticket-col" aria-hidden="true" />
                        </div>

                        {error && <p className="clk-mk-form-error">{error}</p>}
                    </div>

                    <div className="clk-mk-new-ticket-footer">
                        <button type="button" className="clk-mk-new-ticket-btn clk-mk-new-ticket-btn--secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="clk-mk-new-ticket-btn clk-mk-new-ticket-btn--primary">
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MockNewTicketModal;
